import { DEVICE_TYPES } from "./consts";
import { RESPONSIVE_FIELDS_BY_BLOCK_TYPE, PADDING_FIELD_KEYS, TRANSFORM_FIELD_KEYS } from "./styleConfig";
/**
 * Formats a transform value to Tailwind class.
 * scale: 110 -> "scale-[1.1]"
 * positionX: 10 -> "translate-x-[10%]", -10 -> "translate-x-[-10%]"
 * positionY: -5 -> "translate-y-[-5%]"
 */
const formatTransformValue = (value, fieldName) => {
    const transformKey = TRANSFORM_FIELD_KEYS[fieldName];
    if (!transformKey)
        return "";
    if (fieldName === "scale") {
        // Scale: convert percentage to decimal (100 -> 1, 110 -> 1.1)
        if (value === 100)
            return ""; // No class needed for default
        const scaleValue = value / 100;
        const result = `scale-[${scaleValue}]`;
        return result;
    }
    // Position X/Y: negative value goes INSIDE the brackets for Tailwind arbitrary values
    if (value === 0)
        return ""; // No class needed for default
    const result = `${transformKey}-[${value}%]`;
    return result;
};
/**
 * Formats a value with appropriate prefix based on field type.
 * Padding fields get "p-" prefix added to their values.
 */
const formatFieldValue = (value, fieldName) => {
    if (value === "" || value === null || value === undefined)
        return "";
    // Check if this is a transform field (scale, positionX, positionY)
    const isTransformField = fieldName in TRANSFORM_FIELD_KEYS;
    if (isTransformField) {
        return formatTransformValue(Number(value), fieldName);
    }
    // Check if this is a padding field
    if (PADDING_FIELD_KEYS.includes(fieldName)) {
        const strValue = String(value);
        // If value already has p- prefix, return as is
        if (strValue.startsWith("p-") || strValue.startsWith("px-") || strValue.startsWith("py-")) {
            return strValue;
        }
        return `p-${strValue}`;
    }
    return String(value);
};
export const responsiveClasses = (classString, deviceType, isBase = false) => {
    if (classString === null || classString === undefined || classString === "")
        return "";
    // Convert to string in case it's a number or boolean
    const classStr = String(classString);
    const styleAttributes = classStr.split(" ");
    // If this is the base (lowest breakpoint with a value), no prefix needed
    if (isBase) {
        return classStr;
    }
    const responsiveString = styleAttributes
        .map(attr => {
        if (deviceType === "mobile") {
            return attr; // Mobile is always base when it has a value
        }
        else if (deviceType === "tablet") {
            return `md:${attr}`;
        }
        else if (deviceType === "desktop") {
            return `lg:${attr}`;
        }
        else {
            return attr;
        }
    })
        .join(" ");
    return responsiveString;
};
/**
 * Builds responsive Tailwind classes for a given style property from nested breakpoint objects.
 * Uses mobile-first approach: the lowest breakpoint with a value becomes the base (no prefix).
 * @param styles - The styles object containing nested breakpoint objects (desktop, tablet, mobile)
 * @param property - The style property name to extract
 * @param defaultValue - Default value if no style is found
 * @returns Combined responsive class string
 */
export const getResponsiveStyleClasses = (styles, property, defaultValue = "") => {
    var _a, _b, _c;
    const mobileValue = formatFieldValue(((_a = styles === null || styles === void 0 ? void 0 : styles.mobile) === null || _a === void 0 ? void 0 : _a[property]) || "", property);
    const tabletValue = formatFieldValue(((_b = styles === null || styles === void 0 ? void 0 : styles.tablet) === null || _b === void 0 ? void 0 : _b[property]) || "", property);
    const desktopValue = formatFieldValue(((_c = styles === null || styles === void 0 ? void 0 : styles.desktop) === null || _c === void 0 ? void 0 : _c[property]) || "", property);
    const formattedDefault = formatFieldValue(defaultValue, property);
    const classes = [];
    // Determine which breakpoint is the base (lowest with a value)
    // Priority: mobile -> tablet -> desktop -> defaultValue
    if (mobileValue) {
        // Mobile is base - no prefix
        classes.push(responsiveClasses(mobileValue, DEVICE_TYPES.MOBILE, true));
        if (tabletValue)
            classes.push(responsiveClasses(tabletValue, DEVICE_TYPES.TABLET, false));
        if (desktopValue)
            classes.push(responsiveClasses(desktopValue, DEVICE_TYPES.DESKTOP, false));
    }
    else if (tabletValue) {
        // Tablet is base - no prefix
        classes.push(responsiveClasses(tabletValue, DEVICE_TYPES.TABLET, true));
        if (desktopValue)
            classes.push(responsiveClasses(desktopValue, DEVICE_TYPES.DESKTOP, false));
    }
    else if (desktopValue) {
        // Desktop is base - no prefix
        classes.push(responsiveClasses(desktopValue, DEVICE_TYPES.DESKTOP, true));
    }
    else if (formattedDefault) {
        // Use default as base
        classes.push(formattedDefault);
    }
    return classes.join(" ");
};
/**
 * Builds all responsive style classes for a content block type.
 * Uses RESPONSIVE_FIELDS_BY_BLOCK_TYPE to determine which fields are responsive.
 * @param styles - The styles object containing nested breakpoint objects
 * @param contentBlockType - The type of content block (e.g., "media", "pageSection")
 * @param defaults - Default values for each property
 * @returns Object with each responsive property mapped to its combined class string
 */
export const buildResponsiveStyles = (styles, contentBlockType, defaults = {}) => {
    const responsiveFields = RESPONSIVE_FIELDS_BY_BLOCK_TYPE[contentBlockType] || [];
    const result = {};
    responsiveFields.forEach(field => {
        result[field] = getResponsiveStyleClasses(styles, field, defaults[field] || "");
    });
    return result;
};
/**
 * Gets a responsive value for a specific device breakpoint.
 * @param styles - The styles object containing nested breakpoint objects
 * @param property - The style property name
 * @param breakpoint - The device breakpoint to get value for
 * @param defaultValue - Default value if not found
 * @returns The value for the specified breakpoint
 */
export const getResponsiveValue = (styles, property, breakpoint = "desktop", defaultValue) => {
    var _a, _b;
    return (_b = (_a = styles === null || styles === void 0 ? void 0 : styles[breakpoint]) === null || _a === void 0 ? void 0 : _a[property]) !== null && _b !== void 0 ? _b : defaultValue;
};
